import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Search, MoreHorizontal, Power, Users as UsersIcon, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { tenantUsersApi, TenantUser, CreateUserBody, ListUsersQuery } from "@/lib/users-api";
import { departmentsApi, Department } from "@/lib/departments-api";
import { departmentOptions } from "@/lib/picker-options";
import { SearchSelect } from "@/components/SearchSelect";
import { BackendUserRole, BackendUserStatus, RoleMetadata, rolesApi, userStatusToLabel } from "@/lib/platform-api";
import { ApiError } from "@/lib/api";
import { BulkUserImportDialog } from "@/components/BulkUserImportDialog";
import { useAuth } from "@/auth/AuthContext";
import { InviteLinkDialog } from "@/components/InviteLinkDialog";
import { formatDateTimeIn } from "@/lib/datetime";
import { useTenantTimezone } from "@/hooks/useTenantTimezone";
import { TENANT_ROLES, assignableRoles } from "@/lib/roles";

const STATUS_OPTIONS: { value: BackendUserStatus; label: string }[] = [
  { value: "ACTIVE", label: "Active" },
  { value: "DISABLED", label: "Disabled" },
  { value: "PENDING", label: "Pending" },
];


export default function Users() {
  const tz = useTenantTimezone();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");

  const [users, setUsers] = useState<TenantUser[]>([]);
  const [roles, setRoles] = useState<RoleMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  /**
   * Set after a fresh invitation is generated (create or reset on a
   * never-accepted account). Drives the InviteLinkDialog so admins can
   * copy the URL when Clerk's email doesn't deliver.
   */
  const [inviteLink, setInviteLink] = useState<{
    name: string;
    email: string;
    url: string;
    variant: "new" | "reset";
  } | null>(null);

  useEffect(() => {
    rolesApi.list().then(setRoles).catch(() => { /* non-fatal */ });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const q: ListUsersQuery = {
      pageSize: 200,
      search: search || undefined,
      status: statusFilter === "all" ? undefined : (statusFilter as BackendUserStatus),
      roleType: roleFilter === "all" ? undefined : (roleFilter as BackendUserRole),
    };
    tenantUsersApi
      .list(q)
      .then((r) => { if (!cancelled) setUsers(r.data); })
      .catch((e) => { if (!cancelled) toast.error("Failed to load users", { description: errMsg(e) }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [search, statusFilter, roleFilter, reloadKey]);

  const active = users.filter(u => u.status === "ACTIVE").length;
  const pending = users.filter(u => u.status === "PENDING").length;
  const disabled = users.filter(u => u.status === "DISABLED").length;

  const handleToggle = async (u: TenantUser) => {
    try {
      if (u.status === "DISABLED") await tenantUsersApi.enable(u.id);
      else await tenantUsersApi.disable(u.id);
      toast.success(`${u.name} ${u.status === "DISABLED" ? "enabled" : "disabled"}`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Action failed", { description: errMsg(e) });
    }
  };

  const tenantRoleOptions = roles.filter((r) => r.scope === "tenant");

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total users</div><div className="mt-1 font-display text-xl font-bold">{users.length}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Active</div><div className="mt-1 font-display text-xl font-bold text-success">{active}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Pending</div><div className="mt-1 font-display text-xl font-bold text-warning">{pending}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Disabled</div><div className="mt-1 font-display text-xl font-bold text-destructive">{disabled}</div></Card>
      </div>

      <Card>
        <CardHeader className="border-b pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2"><UsersIcon className="h-5 w-5 text-primary" /><CardTitle className="text-base">Users ({users.length})</CardTitle></div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users..." className="h-9 w-56 pl-8" />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="h-9 w-40"><SelectValue placeholder="Role" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  {(tenantRoleOptions.length > 0 ? tenantRoleOptions : TENANT_ROLES.map(r => ({ key: r.key, label: r.label, scope: "tenant" as const, description: "", assignableByTenantOwner: true })))
                    .map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 w-32"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All status</SelectItem>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <BulkUserImportDialog
                title="Bulk import users"
                onImport={(rows) => tenantUsersApi.bulkImport(rows)}
                onCompleted={() => setReloadKey((k) => k + 1)}
              />
              <AddUserDialog
                roles={roles}
                onCreated={() => setReloadKey((k) => k + 1)}
                onInviteGenerated={(name, email, url) =>
                  setInviteLink({ name, email, url, variant: "new" })
                }
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead>
                <TableHead>Department</TableHead><TableHead>Scope</TableHead><TableHead>Status</TableHead><TableHead>Last login</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading users...</TableCell></TableRow>}
              {!loading && users.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">No users match your filters.</TableCell></TableRow>}
              {!loading && users.map(u => (
                <TableRow key={u.id}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent text-[10px] font-semibold text-white">{u.name.split(" ").map(n => n[0]).slice(0, 2).join("")}</div>
                      <span className="text-sm font-medium">{u.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                  <TableCell className="text-sm">{u.roleLabel ?? u.roleType}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.departmentPath ?? u.department ?? "—"}</TableCell>
                  <TableCell>
                    {u.scopeDepartmentId ? (
                      <Badge variant="outline" className="max-w-44 truncate border-warning/30 bg-warning/10 text-warning" title={u.scopeDepartmentPath ?? undefined}>
                        {u.scopeDepartmentPath ?? "Scoped"}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">All</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      u.status === "ACTIVE" ? "border-success/30 bg-success/10 text-success" :
                      u.status === "PENDING" ? "border-warning/30 bg-warning/10 text-warning" :
                      "border-destructive/30 bg-destructive/10 text-destructive"
                    }>{userStatusToLabel(u.status)}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDateTimeIn(u.lastLoginAt, tz)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem onClick={() => handleToggle(u)}>
                          <Power className="mr-2 h-3.5 w-3.5" /> {u.status === "DISABLED" ? "Enable" : "Disable"} user
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {inviteLink && (
        <InviteLinkDialog
          open={!!inviteLink}
          onClose={() => setInviteLink(null)}
          recipientName={inviteLink.name}
          recipientEmail={inviteLink.email}
          inviteUrl={inviteLink.url}
          variant={inviteLink.variant}
        />
      )}
    </div>
  );
}

function AddUserDialog({
  roles,
  onCreated,
  onInviteGenerated,
}: {
  roles: RoleMetadata[];
  onCreated: () => void;
  onInviteGenerated: (name: string, email: string, url: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const deptChoices = useMemo(() => departmentOptions(departments), [departments]);
  // A scope covers the unit's sub-units too, so the list says so.
  const scopeChoices = useMemo(() => departmentOptions(departments, { includesSubunits: true }), [departments]);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", departmentId: "", scopeDepartmentId: "",
    roleType: "HR_MANAGER" as BackendUserRole,
  });
  // Only the tenant owner may grant visibility scopes (BR-080).
  const { user: me } = useAuth();
  const canAssignScope = me?.backendRoleType === "TENANT_OWNER";
  const tenantRoles = roles.filter(r => r.scope === "tenant");

  useEffect(() => {
    if (!open) return;
    departmentsApi.listAll()
      .then(setDepartments)
      .catch(() => { /* non-fatal — picker just stays empty */ });
  }, [open]);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email required"); return; }
    setSubmitting(true);
    try {
      const body: CreateUserBody = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        departmentId: form.departmentId || undefined,
        scopeDepartmentId: form.scopeDepartmentId || undefined,
        roleType: form.roleType,
      };
      const res = await tenantUsersApi.create(body);
      setOpen(false);
      setForm({ name: "", email: "", phone: "", departmentId: "", scopeDepartmentId: "", roleType: "HR_MANAGER" });
      onCreated();
      if (res.linkedToExisting) {
        // Same person already had a Clerk identity (e.g. they're an
        // Employee on the mobile app). No invitation needed; they keep
        // using their existing password.
        toast.success(`${res.name} added to dashboard`, {
          description: `Linked to their existing account (${res.email}). They sign in with their current password.`,
          duration: 8000,
        });
      } else if (res.inviteUrl) {
        onInviteGenerated(res.name, res.email, res.inviteUrl);
      } else {
        toast.success(`${res.name} created`, {
          description: `Invitation email sent to ${res.email}.`,
          duration: 6000,
        });
      }
    } catch (e) {
      toast.error("Could not create user", { description: errMsg(e) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-gradient-primary hover:opacity-90">
          <Plus className="mr-1.5 h-4 w-4" /> Add user
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add user</DialogTitle>
          <DialogDescription>
            Create a new admin user. They'll receive an email invitation to set up their account.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="grid gap-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" /></div>
          <div className="grid gap-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="user@example.com" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+60..." /></div>
            <div className="grid gap-1.5">
              <Label>Department</Label>
              <SearchSelect
                label="Department"
                value={form.departmentId || "none"}
                onChange={(v) => setForm({ ...form, departmentId: v === "none" ? "" : v })}
                options={deptChoices}
                emptyOption={{ value: "none", label: "None" }}
              />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Role</Label>
            <Select value={form.roleType} onValueChange={(v) => setForm({ ...form, roleType: v as BackendUserRole })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {assignableRoles(tenantRoles.length > 0 ? tenantRoles : TENANT_ROLES.map(r => ({ key: r.key, label: r.label, scope: "tenant" as const, description: "", assignableByTenantOwner: true })))
                  .map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {canAssignScope && (
            <div className="grid gap-1.5">
              <Label>Visibility scope</Label>
              <SearchSelect
                label="Visibility scope"
                value={form.scopeDepartmentId || "all"}
                onChange={(v) => setForm({ ...form, scopeDepartmentId: v === "all" ? "" : v })}
                options={scopeChoices}
                emptyOption={{ value: "all", label: "Entire organization" }}
              />
              <p className="text-[11px] text-muted-foreground">
                Scoped admins only see employees, attendance and requests inside this unit
                (and its sub-units), and cannot manage users or settings.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Unexpected error";
}
